import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { diffAddressesForUpdate } from '@/shared/utils/diffAddressesForUpdate'
import type { PortalUpdateOrganizationInput } from '@/graphql/generated/graphql'
import { routes } from '@/shared/constants'
import { useMyOrganization } from './hooks/useMyOrganization'
import { useUpdateOrganization } from './hooks/useUpdateOrganization'
import { OrganizationSkeleton } from './components/OrganizationSkeleton'
import { OrganizationEmptyState } from './components/OrganizationEmptyState'
import {
  ORGANIZATION_EDIT_FORM_ID,
  OrganizationEditForm,
} from './components/edit-form/OrganizationEditForm'

/**
 * Página de EDICIÓN de la organización. La vista (lectura) vive ahora en
 * "Mis negocios"; al pulsar el lápiz se entra directamente aquí a editar.
 */
export function OrganizationPage() {
  const navigate = useNavigate()
  const { organization, loading, error, refetch } = useMyOrganization()
  const { updateOrganization, loading: saving } = useUpdateOrganization({
    onSuccess: () => {
      toast.success('Cambios guardados')
      navigate(routes.app.businesses.list)
    },
  })

  if (loading) return <OrganizationSkeleton />
  if (error) return <QueryErrorScreen error={error} onRetry={() => refetch()} />
  if (!organization) {
    return <OrganizationEmptyState onCreate={() => navigate(routes.app.organization.new)} />
  }

  return (
    <div className="space-y-6">
      <Link
        to={routes.app.businesses.list}
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Mis negocios
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">
            Editar organización
          </h1>
          <p className="mt-1 text-sm text-ink-muted">Actualiza los datos de tu organización.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(routes.app.businesses.list)} disabled={saving}>
            <X className="h-4 w-4" />
            Cancelar
          </Button>
          <Button type="submit" form={ORGANIZATION_EDIT_FORM_ID} variant="primary" disabled={saving}>
            <Check className="h-4 w-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <OrganizationEditForm
        organization={organization}
        disabled={saving}
        onSubmit={async (values, dirty) => {
          const input: PortalUpdateOrganizationInput = {
            organizationId: organization.id ?? '',
          }
          if (dirty.legalName) input.legalName = values.legalName
          if (dirty.description) input.description = values.description || undefined
          if (dirty.logo) input.logo = values.logo ?? ''
          if (dirty.addresses) {
            const addresses = diffAddressesForUpdate(values.addresses ?? [])
            if (addresses.length) input.addresses = addresses
          }

          const changed =
            dirty.legalName || dirty.description || dirty.logo || Boolean(dirty.addresses)
          if (!changed) {
            toast('No hay cambios que guardar')
            navigate(routes.app.businesses.list)
            return
          }
          await updateOrganization(input)
        }}
      />
    </div>
  )
}
